import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { GameCard } from "../components/GameCard";
import { RatingStars } from "../components/RatingStars";
import { findGameBySlug, relatedGames } from "../data/placeholderGames";
import { resolveGamePlayUrl } from "../lib/gameUrl";
import { ARCHETYPE_LABELS } from "../types/Game";
import { NotFoundRoute } from "./notFound";

/**
 * Game detail page — `/game/:slug`. Large preview hero, meta sidebar, action
 * buttons, and related games grid.
 *
 * CRITICAL: The Play button uses `window.open()` to launch the game in a new
 * browser tab/window. This is an architecture decision — games run in their own
 * window, NOT within the portal SPA.
 * See: .forge/workflows/work/engineering/CRITICAL-games-in-new-windows.md
 */
export function GameDetailRoute() {
	const { slug } = useParams<{ slug: string }>();
	const [copied, setCopied] = useState(false);
	const game = slug ? findGameBySlug(slug) : undefined;

	useEffect(() => {
		if (game) {
			document.title = `${game.title} by ${game.creator} · GenieWorlds`;
		} else {
			document.title = "Game not found · GenieWorlds";
		}
	}, [game]);

	if (!game) {
		return <NotFoundRoute />;
	}

	// Re-bind after the guard so closures see the narrowed type (Game, not
	// Game | undefined). TypeScript doesn't carry control-flow narrowing into
	// function declarations.
	const g = game;

	const related = relatedGames(g.slug, 4);

	function formatPlays(n: number): string {
		if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
		if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
		return String(n);
	}

	/**
	 * CRITICAL: Open game in a new browser window/tab.
	 * Per architecture decision, games run in their own window, NOT within the
	 * portal SPA. This enables fullscreen, prevents portal state loss, and
	 * mirrors production architecture (games.genieworlds.games/<slug>/).
	 *
	 * In local dev, games are served by the game-server app at port 5173.
	 * In production, games live at games.genieworlds.games/<slug>/.
	 */
	const gamePlayUrl = resolveGamePlayUrl(g.slug, g.archetype);
	const canPlay = gamePlayUrl !== null;

	function handlePlay() {
		if (!gamePlayUrl) return;
		window.open(gamePlayUrl, "_blank", "noopener,noreferrer");
	}

	async function handleShare() {
		const url = window.location.href;
		try {
			if (navigator.share) {
				await navigator.share({
					title: g.title,
					text: g.description,
					url,
				});
				return;
			}
		} catch {
			// User cancelled or share failed — fall through to clipboard
		}
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			// Last resort — no-op
		}
	}

	return (
		<div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8 space-y-8">
			{/* Breadcrumb */}
			<nav
				className="flex items-center gap-2 text-xs text-ink-faint"
				aria-label="Breadcrumb"
			>
				<Link to="/" className="hover:text-ink transition-colors">
					Home
				</Link>
				<span>/</span>
				<Link
					to="/browse"
					className="hover:text-ink transition-colors"
				>
					Browse
				</Link>
				<span>/</span>
				<Link
					to={`/browse?category=${g.archetype}`}
					className="hover:text-ink transition-colors"
				>
					{ARCHETYPE_LABELS[g.archetype]}
				</Link>
				<span>/</span>
				<span className="text-ink-muted">{g.title}</span>
			</nav>

			<div className="grid md:grid-cols-3 gap-6 md:gap-8">
				{/* Main column — preview + actions */}
				<div className="md:col-span-2 space-y-5">
					{/* Game preview / hero area */}
					<div
						className="relative w-full aspect-[16/10] rounded-xl overflow-hidden border border-border"
						style={{ backgroundColor: g.thumbnail.bg }}
					>
						<div className="absolute inset-0 flex items-center justify-center">
							<span
								className="text-[8rem] md:text-[10rem]"
								style={{
									filter: `drop-shadow(0 0 48px ${g.thumbnail.fg}66)`,
								}}
								aria-hidden="true"
							>
								{g.thumbnail.emoji}
							</span>
						</div>
						<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

						{/* Big centered play button — click to open game in new window */}
						<button
							type="button"
							onClick={handlePlay}
							disabled={!canPlay}
							className={`absolute inset-0 flex items-center justify-center group ${canPlay ? "cursor-pointer" : "cursor-default"}`}
							aria-label={canPlay ? `Play ${g.title}` : `${g.title} — coming soon`}
						>
							<div className={`h-20 w-20 md:h-24 md:w-24 rounded-full flex items-center justify-center transition-all duration-300 ${canPlay ? "bg-primary/90 text-background shadow-[0_0_60px_rgba(0,255,136,0.4)] group-hover:shadow-[0_0_80px_rgba(0,255,136,0.6)] group-hover:scale-105" : "bg-ink-faint/40 text-ink-muted"}`}>
								{canPlay ? (
									<svg
										viewBox="0 0 24 24"
										fill="currentColor"
										className="h-10 w-10 md:h-12 md:w-12 ml-1"
										aria-hidden="true"
									>
										<path d="M8 5v14l11-7z" />
									</svg>
								) : (
									<span className="text-xs font-bold uppercase tracking-wider">Soon</span>
								)}
							</div>
						</button>
					</div>

					{/* Action buttons */}
					<div className="flex flex-wrap gap-3">
						<button
							type="button"
							onClick={handlePlay}
							disabled={!canPlay}
							className={`inline-flex items-center gap-2.5 h-12 px-7 rounded-full font-bold transition-all duration-300 ${canPlay ? "bg-primary text-background hover:shadow-[0_0_40px_rgba(0,255,136,0.4)]" : "bg-surface border border-border text-ink-muted cursor-not-allowed"}`}
						>
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								className="h-5 w-5"
								aria-hidden="true"
							>
								<path d="M8 5v14l11-7z" />
							</svg>
							{canPlay ? "Play Now" : "Coming Soon"}
						</button>
						<button
							type="button"
							onClick={handleShare}
							className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-surface border border-border text-ink font-semibold hover:border-primary/40 hover:text-primary transition-all duration-200"
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<circle cx="18" cy="5" r="3" />
								<circle cx="6" cy="12" r="3" />
								<circle cx="18" cy="19" r="3" />
								<line
									x1="8.59"
									y1="13.51"
									x2="15.42"
									y2="17.49"
								/>
								<line
									x1="15.41"
									y1="6.51"
									x2="8.59"
									y2="10.49"
								/>
							</svg>
							{copied ? "Copied!" : "Share"}
						</button>
						<a
							href="https://genieforge.io"
							className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-purple/10 border border-purple/30 text-purple font-semibold hover:bg-purple/20 transition-all duration-200"
						>
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<path d="M12 2 15 9l7 .8-5.3 4.7L18.2 22 12 18.3 5.8 22l1.5-7.5L2 9.8 9 9z" />
							</svg>
							Remix
						</a>
					</div>
				</div>

				{/* Sidebar — game info */}
				<aside className="space-y-6">
					<div>
						<span className="inline-block text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-surface border border-border text-ink-muted mb-3">
							{ARCHETYPE_LABELS[g.archetype]}
						</span>
						<h1 className="font-display font-extrabold text-3xl md:text-4xl text-ink leading-tight">
							{g.title}
						</h1>
						<p className="text-ink-muted mt-1.5 text-sm">
							by{" "}
							<span className="text-ink font-medium">
								{g.creator}
							</span>
						</p>
					</div>

					{/* Stats */}
					<div className="flex items-center gap-5">
						<div className="flex items-center gap-2">
							<RatingStars rating={g.rating} size="md" />
							<span className="font-bold text-ink">
								{g.rating.toFixed(1)}
							</span>
						</div>
						<div className="flex items-center gap-1.5 text-sm text-ink-muted">
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10zm0-2a3 3 0 100-6 3 3 0 000 6z" />
							</svg>
							<span>{formatPlays(g.plays)} plays</span>
						</div>
					</div>

					<p className="text-ink-muted leading-relaxed">
						{g.description}
					</p>

					{/* Tags */}
					<div className="flex flex-wrap gap-2">
						{g.tags.map((tag) => (
							<Link
								key={tag}
								to={`/search?q=${encodeURIComponent(tag)}`}
								className="px-3 py-1.5 rounded-full text-xs font-medium bg-surface border border-border text-ink-muted hover:text-primary hover:border-primary/30 transition-all duration-200"
							>
								#{tag}
							</Link>
						))}
					</div>

					<div className="pt-4 border-t border-border text-xs text-ink-faint">
						Published{" "}
						{new Date(g.publishedAt).toLocaleDateString(
							undefined,
							{
								year: "numeric",
								month: "long",
								day: "numeric",
							},
						)}
					</div>
				</aside>
			</div>

			{/* Related games */}
			{related.length > 0 && (
				<section className="space-y-5 pt-8 border-t border-border">
					<h2 className="font-display font-bold text-xl md:text-2xl text-ink">
						You might also like
					</h2>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
						{related.map((g) => (
							<GameCard key={g.id} game={g} />
						))}
					</div>
				</section>
			)}
		</div>
	);
}
